import {
  cloneElement,
  useCallback,
  useEffect,
  useId,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import clsx from 'clsx';

import styles from './Tooltip.module.css';

export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

export interface TooltipProps {
  /** The tooltip text. Supplementary — never the only place a fact appears. */
  label: ReactNode;
  children: ReactElement<{ 'aria-describedby'?: string }>;
  placement?: TooltipPlacement;
  /** Suppress without unmounting the trigger (a disabled control). */
  disabled?: boolean;
  className?: string;
}

/**
 * Hover AND keyboard focus, Escape to dismiss (WCAG 1.4.13). The tooltip is a
 * description, not a label: it is wired with `aria-describedby` onto the trigger
 * itself, so the trigger keeps its own accessible name.
 *
 * No open delay: a 100ms timer makes the tooltip feel unreliable on a dense
 * canvas and makes every test that touches it timing-dependent.
 */
export function Tooltip({
  label,
  children,
  placement = 'top',
  disabled = false,
  className,
}: TooltipProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const show = useCallback(() => {
    if (!disabled && !dismissed) setOpen(true);
  }, [disabled, dismissed]);

  const hide = useCallback(() => {
    setOpen(false);
    setDismissed(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    // A pointer-triggered tooltip has no focus inside it, so Escape has to be
    // caught at the document.
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false);
        setDismissed(true);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const trigger = cloneElement(children, {
    'aria-describedby': open ? id : children.props['aria-describedby'],
  });

  return (
    <span
      className={clsx(styles.wrapper, className)}
      onPointerEnter={show}
      onPointerLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {trigger}
      {open && (
        <span role="tooltip" id={id} className={clsx(styles.tooltip, styles[placement])}>
          {label}
        </span>
      )}
    </span>
  );
}
